// apps/admin/src/pages/StagingFilesPage.tsx
//
// Route 2 of the email-ingestion architectural fix (PR 4/4).
//
// Surfaces every storage object under `onboarding_staging/{c}/` and joins
// against the `upload_intents` collection to flag orphans (files with no
// intent). Linked from PR #782's Google Chat alert action button
// (`email_ingestion_staging_aged`) — when the founder clicks "View in
// Admin", they land here.
//
// Auto-refreshes every 60s while the page is mounted.

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { doc, getDoc, getDocs, collection, query } from 'firebase/firestore';
import { db, functions } from '../firebaseConfig';
import {
  ChevronLeft,
  RefreshCw,
  FileWarning,
  CheckCircle2,
  Search,
  Send,
  Trash2,
  ChevronDown,
  ChevronRight,
  FileText,
} from 'lucide-react';
import { toast } from 'react-hot-toast';

interface StagedFileEntry {
  filename: string;
  storagePath: string;
  sizeBytes: number;
  timeCreated: string;
  ageMinutes: number;
  hasIntent: boolean;
  intentId: string | null;
  intentStatus: string | null;
}

interface InspectResult {
  filename: string;
  entityCode: string | null;
  match: {
    matched: boolean;
    aggregatorCode: string | null;
    aggregatorName: string | null;
    reason: string;
    runInfo: any;
  };
  mapping: {
    exists: boolean;
    templateId: string | null;
    officeId: string | null;
    officeName: string | null;
    status: string | null;
  };
}

interface Office {
  id: string;
  name: string;
}

interface FileTemplate {
  id: string;
  name: string;
}

function formatAge(minutes: number): string {
  if (minutes < 1) return '<1m';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const remMin = minutes % 60;
  if (hours < 24) return `${hours}h ${remMin}m`;
  const days = Math.floor(hours / 24);
  const remHrs = hours % 24;
  return `${days}d ${remHrs}h`;
}

function formatBytes(b: number): string {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}

const StagingFilesPage: React.FC = () => {
  const { companyId } = useParams<{ companyId: string }>();
  const [companyName, setCompanyName] = useState<string>('');
  const [files, setFiles] = useState<StagedFileEntry[]>([]);
  const [orphanCount, setOrphanCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showOnlyOrphans, setShowOnlyOrphans] = useState(true);

  const [offices, setOffices] = useState<Office[]>([]);
  const [templates, setTemplates] = useState<FileTemplate[]>([]);

  // Per-row inspect cache
  const [inspectMap, setInspectMap] = useState<Record<string, InspectResult>>(
    {},
  );
  const [inspectLoading, setInspectLoading] = useState<Record<string, boolean>>(
    {},
  );
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  // Per-row force-route form state
  const [routeForm, setRouteForm] = useState<
    Record<string, { officeId: string; templateId: string }>
  >({});
  const [actionInFlight, setActionInFlight] = useState<string | null>(null);

  const autoRefreshTimerRef = useRef<ReturnType<typeof setInterval> | null>(
    null,
  );

  const loadFiles = useCallback(
    async (initial: boolean) => {
      if (!companyId) return;
      if (initial) setLoading(true);
      else setRefreshing(true);
      try {
        const fn = httpsCallable(functions, 'listStagedUnmatchedFiles');
        const result = (await fn({ companyId })) as any;
        if (!result?.data?.success) {
          toast.error('Failed to load staging files');
          return;
        }
        setFiles(result.data.files || []);
        setOrphanCount(result.data.orphanCount || 0);
        setTotalCount(result.data.totalCount || 0);
      } catch (err) {
        console.error('[StagingFilesPage] loadFiles failed:', err);
        toast.error('Failed to load staging files');
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [companyId],
  );

  // One-time load of offices, templates, company name.
  useEffect(() => {
    if (!companyId) return;
    void (async () => {
      try {
        const companySnap = await getDoc(doc(db, `companies/${companyId}`));
        if (companySnap.exists()) {
          setCompanyName(
            companySnap.data().companyName ||
              companySnap.data().name ||
              companyId,
          );
        }

        const officesSnap = await getDocs(
          query(collection(db, `companies/${companyId}/offices`)),
        );
        setOffices(
          officesSnap.docs.map((d) => ({
            id: d.id,
            name: d.data().name || d.id,
          })),
        );

        const getTemplates = httpsCallable(functions, 'getTemplates');
        const tres = (await getTemplates({})) as any;
        if (tres?.data?.success) {
          setTemplates(
            (tres.data.templates as any[]).map((t) => ({
              id: t.id,
              name: t.name || t.templateName || t.id,
            })),
          );
        }
      } catch (err) {
        console.error('[StagingFilesPage] one-time load failed:', err);
      }
    })();
  }, [companyId]);

  useEffect(() => {
    void loadFiles(true);
  }, [loadFiles]);

  // Auto-refresh every 60s.
  useEffect(() => {
    autoRefreshTimerRef.current = setInterval(() => {
      void loadFiles(false);
    }, 60_000);
    return () => {
      if (autoRefreshTimerRef.current) {
        clearInterval(autoRefreshTimerRef.current);
      }
    };
  }, [loadFiles]);

  const toggleExpand = async (entry: StagedFileEntry) => {
    const key = entry.storagePath;
    const next = new Set(expandedRows);
    if (next.has(key)) {
      next.delete(key);
      setExpandedRows(next);
      return;
    }
    next.add(key);
    setExpandedRows(next);

    // Lazy-fetch inspect.
    if (!inspectMap[key] && !inspectLoading[key]) {
      setInspectLoading((prev) => ({ ...prev, [key]: true }));
      try {
        const fn = httpsCallable(functions, 'inspectStagedFile');
        const result = (await fn({
          companyId,
          storagePath: entry.storagePath,
        })) as any;
        if (result?.data?.success) {
          const r: InspectResult = {
            filename: result.data.filename,
            entityCode: result.data.entityCode,
            match: result.data.match,
            mapping: result.data.mapping,
          };
          setInspectMap((prev) => ({ ...prev, [key]: r }));
          // Pre-fill the route form if the inspector suggested mapping.
          if (r.mapping.exists && r.mapping.officeId && r.mapping.templateId) {
            setRouteForm((prev) => ({
              ...prev,
              [key]: {
                officeId: r.mapping.officeId!,
                templateId: r.mapping.templateId!,
              },
            }));
          }
        }
      } catch (err) {
        console.error('[StagingFilesPage] inspectStagedFile failed:', err);
        toast.error('Inspect failed');
      } finally {
        setInspectLoading((prev) => ({ ...prev, [key]: false }));
      }
    }
  };

  const handleForceRoute = async (entry: StagedFileEntry) => {
    if (!companyId) return;
    const key = entry.storagePath;
    const form = routeForm[key];
    if (!form?.officeId || !form?.templateId) {
      toast.error('Pick office + template before force-routing');
      return;
    }
    setActionInFlight(key);
    try {
      const fn = httpsCallable(functions, 'forceRouteStagedFile');
      const result = (await fn({
        companyId,
        storagePath: entry.storagePath,
        templateId: form.templateId,
        officeId: form.officeId,
      })) as any;
      if (result?.data?.success) {
        toast.success(
          result.data.idempotent
            ? `Already routed (intent ${result.data.uploadId})`
            : `Force-routed ${entry.filename}`,
        );
        await loadFiles(false);
      } else {
        toast.error('Force-route failed');
      }
    } catch (err) {
      console.error('[StagingFilesPage] forceRouteStagedFile failed:', err);
      toast.error(`Force-route failed: ${(err as Error).message}`);
    } finally {
      setActionInFlight(null);
    }
  };

  const handleDiscard = async (entry: StagedFileEntry) => {
    if (!companyId) return;
    const reason = window.prompt(
      `Discard ${entry.filename}?\n\nOptional reason (logged for audit trail):`,
      '',
    );
    // null === user clicked cancel; empty string === confirmed with no reason.
    if (reason === null) return;
    const key = entry.storagePath;
    setActionInFlight(key);
    try {
      const fn = httpsCallable(functions, 'discardStagedFile');
      const result = (await fn({
        companyId,
        storagePath: entry.storagePath,
        ...(reason ? { reason } : {}),
      })) as any;
      if (result?.data?.success) {
        toast.success(`Discarded ${entry.filename}`);
        await loadFiles(false);
      } else {
        toast.error('Discard failed');
      }
    } catch (err) {
      console.error('[StagingFilesPage] discardStagedFile failed:', err);
      toast.error(`Discard failed: ${(err as Error).message}`);
    } finally {
      setActionInFlight(null);
    }
  };

  const visibleFiles = showOnlyOrphans ? files.filter((f) => !f.hasIntent) : files;

  if (!companyId) {
    return (
      <div className="text-slate-400">No companyId in route — invalid URL.</div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <Link
            to={`/companies/${companyId}`}
            className="inline-flex items-center gap-1 text-sm text-slate-400 hover:text-slate-200 mb-2"
          >
            <ChevronLeft className="w-4 h-4" />
            Back to {companyName || 'company'}
          </Link>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <FileWarning className="w-6 h-6 text-amber-400" />
            Staging Files
          </h1>
          <p className="text-slate-400 text-sm mt-1">
            Files in <code className="text-slate-300">onboarding_staging/</code>{' '}
            that have not been routed to the pipeline.
            {companyName && (
              <span className="ml-1 text-slate-300">{companyName}</span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="inline-flex items-center gap-2 text-sm text-slate-300">
            <input
              type="checkbox"
              checked={showOnlyOrphans}
              onChange={(e) => setShowOnlyOrphans(e.target.checked)}
              className="rounded border-slate-600 bg-slate-700"
            />
            Orphans only
          </label>
          <button
            onClick={() => void loadFiles(false)}
            disabled={refreshing || loading}
            className="inline-flex items-center gap-2 px-3 py-2 bg-slate-800 border border-slate-700 text-slate-200 rounded-md hover:bg-slate-700 disabled:opacity-50 text-sm"
          >
            <RefreshCw
              className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`}
            />
            Refresh
          </button>
        </div>
      </div>

      {/* Summary line */}
      {!loading && (
        <div className="text-sm text-slate-400">
          <span className="text-slate-200 font-medium">{orphanCount}</span> orphan
          file{orphanCount === 1 ? '' : 's'} of{' '}
          <span className="text-slate-200 font-medium">{totalCount}</span> total
          in staging. Auto-refreshes every 60s.
        </div>
      )}

      {/* Body */}
      {loading ? (
        <div className="text-slate-400">Loading staging files…</div>
      ) : visibleFiles.length === 0 ? (
        <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-8 text-center">
          <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-white mb-1">
            No stuck files
          </h3>
          <p className="text-slate-400 text-sm">
            {showOnlyOrphans
              ? 'No orphaned files in staging.'
              : 'No files in staging.'}
          </p>
        </div>
      ) : (
        <div className="bg-slate-800/50 border border-slate-700 rounded-lg overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="text-left text-slate-400 text-sm border-b border-slate-700 bg-slate-900/40">
                <th className="px-4 py-3 w-8"></th>
                <th className="px-4 py-3">Filename</th>
                <th className="px-4 py-3">Age</th>
                <th className="px-4 py-3">Size</th>
                <th className="px-4 py-3">Intent</th>
                <th className="px-4 py-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {visibleFiles.map((entry) => {
                const key = entry.storagePath;
                const isExpanded = expandedRows.has(key);
                const inspect = inspectMap[key];
                const inflight = actionInFlight === key;

                return (
                  <React.Fragment key={key}>
                    <tr className="border-b border-slate-700/50 hover:bg-slate-800/40">
                      <td className="px-2 py-3">
                        <button
                          onClick={() => void toggleExpand(entry)}
                          className="text-slate-400 hover:text-slate-200 p-1"
                          aria-label={isExpanded ? 'Collapse' : 'Expand'}
                        >
                          {isExpanded ? (
                            <ChevronDown className="w-4 h-4" />
                          ) : (
                            <ChevronRight className="w-4 h-4" />
                          )}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-slate-200">
                        <div className="flex items-center gap-2">
                          <FileText className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                          <span className="font-mono text-sm">
                            {entry.filename}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-300">
                        {formatAge(entry.ageMinutes)}
                      </td>
                      <td className="px-4 py-3 text-slate-300">
                        {formatBytes(entry.sizeBytes)}
                      </td>
                      <td className="px-4 py-3">
                        {entry.hasIntent ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-600/20 text-emerald-300 rounded text-xs">
                            <CheckCircle2 className="w-3 h-3" />
                            {entry.intentStatus}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-600/20 text-red-300 rounded text-xs">
                            Orphan
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right space-x-2 whitespace-nowrap">
                        <button
                          onClick={() => void toggleExpand(entry)}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-slate-700/40 text-slate-200 hover:bg-slate-700 rounded text-xs"
                          disabled={inflight}
                        >
                          <Search className="w-3 h-3" />
                          Inspect
                        </button>
                        <button
                          onClick={() => void handleDiscard(entry)}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-red-600/10 text-red-300 hover:bg-red-600/20 rounded text-xs disabled:opacity-40"
                          disabled={inflight}
                        >
                          <Trash2 className="w-3 h-3" />
                          Discard
                        </button>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className="border-b border-slate-700/50 bg-slate-900/30">
                        <td></td>
                        <td colSpan={5} className="px-4 py-4">
                          {inspectLoading[key] ? (
                            <div className="text-slate-400 text-sm">
                              Inspecting…
                            </div>
                          ) : inspect ? (
                            <div className="space-y-3 text-sm">
                              <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-slate-300">
                                <div>
                                  <span className="text-slate-500">
                                    Entity code:
                                  </span>{' '}
                                  <span className="font-mono">
                                    {inspect.entityCode || '—'}
                                  </span>
                                </div>
                                <div>
                                  <span className="text-slate-500">
                                    Aggregator match:
                                  </span>{' '}
                                  {inspect.match.matched ? (
                                    <span className="text-emerald-300">
                                      {inspect.match.aggregatorName} (
                                      {inspect.match.aggregatorCode})
                                    </span>
                                  ) : (
                                    <span className="text-red-300">
                                      No match — {inspect.match.reason}
                                    </span>
                                  )}
                                </div>
                                {inspect.match.runInfo && (
                                  <div>
                                    <span className="text-slate-500">
                                      Run info:
                                    </span>{' '}
                                    RUN {inspect.match.runInfo.runNumber},{' '}
                                    {inspect.match.runInfo.periodMonth}
                                  </div>
                                )}
                                <div>
                                  <span className="text-slate-500">
                                    Existing mapping:
                                  </span>{' '}
                                  {inspect.mapping.exists ? (
                                    <span className="text-emerald-300">
                                      {inspect.mapping.officeName} ({inspect.mapping.status})
                                    </span>
                                  ) : (
                                    <span className="text-slate-500">none</span>
                                  )}
                                </div>
                              </div>
                              {/* Force-Route form */}
                              <div className="pt-3 border-t border-slate-700/40">
                                <div className="text-slate-400 text-xs uppercase tracking-wide mb-2">
                                  Force-Route
                                </div>
                                <div className="flex items-end gap-2 flex-wrap">
                                  <div>
                                    <label className="block text-[11px] text-slate-500 mb-0.5">
                                      Office
                                    </label>
                                    <select
                                      value={routeForm[key]?.officeId || ''}
                                      onChange={(e) =>
                                        setRouteForm((prev) => ({
                                          ...prev,
                                          [key]: {
                                            officeId: e.target.value,
                                            templateId:
                                              prev[key]?.templateId || '',
                                          },
                                        }))
                                      }
                                      className="px-2 py-1.5 bg-slate-800 border border-slate-700 rounded text-white text-sm"
                                    >
                                      <option value="">Pick office…</option>
                                      {offices.map((o) => (
                                        <option key={o.id} value={o.id}>
                                          {o.name}
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                  <div>
                                    <label className="block text-[11px] text-slate-500 mb-0.5">
                                      Template
                                    </label>
                                    <select
                                      value={routeForm[key]?.templateId || ''}
                                      onChange={(e) =>
                                        setRouteForm((prev) => ({
                                          ...prev,
                                          [key]: {
                                            officeId:
                                              prev[key]?.officeId || '',
                                            templateId: e.target.value,
                                          },
                                        }))
                                      }
                                      className="px-2 py-1.5 bg-slate-800 border border-slate-700 rounded text-white text-sm"
                                    >
                                      <option value="">Pick template…</option>
                                      {templates.map((t) => (
                                        <option key={t.id} value={t.id}>
                                          {t.name}
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                  <button
                                    onClick={() => void handleForceRoute(entry)}
                                    disabled={
                                      inflight ||
                                      !routeForm[key]?.officeId ||
                                      !routeForm[key]?.templateId
                                    }
                                    className="inline-flex items-center gap-1 px-3 py-1.5 bg-indigo-600/30 text-indigo-200 hover:bg-indigo-600/50 disabled:opacity-40 disabled:cursor-not-allowed rounded text-xs"
                                  >
                                    <Send className="w-3 h-3" />
                                    Force-Route
                                  </button>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="text-slate-500 text-sm">
                              Click Inspect to load parsed metadata.
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default StagingFilesPage;
